import { defineStore } from 'pinia';
import { i18n } from '@/plugins/i18n';
import { vuetify } from '@/plugins/vuetify';
import { getLanguageTranslations, getOverrides } from '@shared/translations';

const DEFAULT_LANGUAGE = 'en';

export interface LanguageItem {
  default: boolean;
  id: number;
  name: string;
  ordering: number;
  rtl: boolean;
  value: string;
}

interface LanguageResponses {
  name: string;
  type: string;
  responses: LanguageItem[];
  options: Record<string, unknown>;
}

interface LanguageState {
  selected: string;
  source: 'fallback' | 'user' | 'query' | 'config';
  rtl: boolean;
  all: LanguageItem[];
  initialSetupCompleted: boolean;
}

// helper functions
const isRtl = (lang: string, languages: LanguageItem[]): boolean => {
  const selectedLanguage = languages?.find((language) => language.value === lang);
  if (selectedLanguage) {
    return selectedLanguage.rtl;
  }
  // fallback to defaults
  return lang === 'ar-EG' || lang === 'ar' || lang === 'ur';
};

const formatLocale = (lang: string): string => {
  if (lang === 'hi') return 'hi-u-nu-deva';
  if (lang === 'ar') return 'ar-u-nu-arabext';
  return lang;
};

const getFallbackLocale = (lang: string): string => {
  const split = lang.split('-');
  return split.length > 1 ? formatLocale(split[0]) : lang;
};

type RouterQuery = Record<string, string | Array<string | null> | null>

/**
 * router query object may take many forms
 * this function compresses into a single, possibly empty string
 * handles the following possiblities:
 *   "" - no query for the given key. key is undefined in the query record
 *   "?key" - value for the record is null
 *   "?key=" - value for the record is empty string
 *   "?key=val" - value for the record is a string "val"
 *   "?key=val1&key=val2" - value for the record is an array of strings ["val1", "val2"]
 *     in this case, returns the first instance: "val1"
 * @param query the raw router query object
 * @param key the key to fetch
 * @returns string that matches the first query instance, possibly empty
 */
const getQueryValue = (query: RouterQuery, key: string): string => {
  const value = query[key];
  if (!value) return '';
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  return value;
};

export const useLanguageStore = defineStore('language', {
  persist: true,
  state: (): LanguageState => ({
    selected: DEFAULT_LANGUAGE,
    source: 'fallback',
    rtl: false,
    all: [],
    initialSetupCompleted: false,
  }),

  getters: {
    responses(state): LanguageResponses[] {
      return [{
        name: 'languages',
        type: 'select',
        responses: state.all,
        options: {},
      }];
    },
  },

  actions: {
    load(engagementCode?: string): void {
      const baseTranslations = getLanguageTranslations();
      Object.entries(baseTranslations).forEach(([language, messages]) => {
        i18n.setLocaleMessage(formatLocale(language), messages);
      });

      if (engagementCode) {
        let overrides = getOverrides(engagementCode);
        Object.entries(overrides).forEach(([language, messages]) => {
          i18n.mergeLocaleMessage(formatLocale(language), messages);
        });
      }
    },

    set(language?: string, routerQuery?: RouterQuery): void {
      let targetSelected = DEFAULT_LANGUAGE;
      let targetSource = 'fallback';

      // check for language set by login page
      const loginLang = getQueryValue(routerQuery || {}, 'loginLang');

      const configDefaultLang = this.all.find((lang) => lang.default)?.value;

      if (language) {
        targetSelected = language;
        targetSource = 'user';
      } else if (loginLang) {
        targetSelected = loginLang;
        targetSource = 'user';
      } else if (this.source === 'user') {
        targetSelected = this.selected;
        targetSource = 'user';
      } else if (configDefaultLang) {
        targetSelected = configDefaultLang;
        targetSource = 'config';
      }

      const configuredForEngagement = this.all.some((lang) => lang.value === targetSelected);

      if (!configuredForEngagement) {
        if (configDefaultLang) {
          targetSelected = configDefaultLang;
          targetSource = 'config';
        } else {
          targetSelected = DEFAULT_LANGUAGE;
          targetSource = 'fallback';
        }
      }

      const rtl = isRtl(targetSelected, this.all);

      // set internal state
      this.selected = targetSelected;
      this.source = targetSource;
      this.rtl = rtl;

      // sync external plugins
      vuetify.framework.rtl = rtl;
      vuetify.framework.lang.current = targetSelected;
      i18n.locale = formatLocale(targetSelected); 
      i18n.fallbackLocale = getFallbackLocale(targetSelected);
    },

    completeInitialSetup(): void {
      this.initialSetupCompleted = true;
    }
  }
})
